/**
 * Tracks which items are currently in viewport using IntersectionObserver,
 * and watches the root subtree with a MutationObserver so elements added
 * later by Vue's v-for start being observed automatically.
 *
 * Items are identified by their `data-index` attribute on DOM elements.
 * Whenever the visible set changes, `onVisibilityChange` is called so the
 * consumer can schedule work (typically a debounced flush).
 */
export class ViewportMonitor {
  readonly visibleItems = new Set<number>()

  private observer: IntersectionObserver | null = null
  private mutationObserver: MutationObserver | null = null
  private pendingObserveRAF: number | null = null
  private observedElements = new WeakSet<Element>()
  private root: HTMLElement | null = null

  constructor(
    private onVisibilityChange: (visibleItems: Set<number>) => void,
    private lookaheadMargin: string = '150% 0px 150% 0px'
  ) {}

  attach(root: HTMLElement): void {
    this.root = root
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        root,
        rootMargin: this.lookaheadMargin,
        threshold: 0,
      }
    )
    this.observeNewElements()

    // Watch for DOM changes (e.g. search filtering adds/removes items from
    // the v-for list). Coalesced via requestAnimationFrame to avoid repeated
    // DOM scans when many mutations fire in quick succession.
    this.mutationObserver = new MutationObserver(() =>
      this.scheduleObserveNewElements()
    )
    this.mutationObserver.observe(root, { childList: true, subtree: true })
  }

  detach(): void {
    this.observer?.disconnect()
    this.observer = null
    this.mutationObserver?.disconnect()
    this.mutationObserver = null
    if (this.pendingObserveRAF !== null) {
      cancelAnimationFrame(this.pendingObserveRAF)
      this.pendingObserveRAF = null
    }
    this.root = null
    this.visibleItems.clear()
    this.observedElements = new WeakSet()
  }

  private scheduleObserveNewElements(): void {
    if (this.pendingObserveRAF !== null) return
    this.pendingObserveRAF = requestAnimationFrame(() => {
      this.pendingObserveRAF = null
      this.observeNewElements()
    })
  }

  private observeNewElements(): void {
    if (!this.root || !this.observer) return

    const elements = this.root.querySelectorAll<HTMLElement>('[data-index]')
    elements.forEach((el) => {
      if (!this.observedElements.has(el)) {
        this.observedElements.add(el)
        this.observer!.observe(el)
      }
    })
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    let hasChanges = false

    for (const entry of entries) {
      const index = parseInt(
        (entry.target as HTMLElement).dataset.index ?? '',
        10
      )
      if (Number.isNaN(index)) continue

      if (entry.isIntersecting) {
        this.visibleItems.add(index)
      } else {
        this.visibleItems.delete(index)
      }
      hasChanges = true
    }

    if (hasChanges) {
      this.onVisibilityChange(this.visibleItems)
    }
  }
}
